import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Report } from './utils';
import TrueImpactModal, {
  modelExtraRadiusStyles,
} from '../../design-system/components/TrueImpactModal';
import { useGraphQLRequest } from '../../auth/hooks/useApiRequest';
import {
  getUnallocatedInvestmentsQuery,
  Task,
  UnallocatedInvestment,
} from 'npo/pages/Tasks/api';
import { useClient } from '../../apollo';
import { RequestState } from '../../auth/hooks/useRequestState';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import TrueImpactLoading from '../TrueImpactLoading';
import {
  buildUnallocatedInvestmentTasks,
  getAllocationTaskDescription,
  getTaskMeta,
} from 'npo/pages/Tasks/utils';
import { useUserActions } from '../../auth/hooks/useUserActions';
import { SPACERS } from '../../design-system/settings/variables/Spacers';
import { StyledTaskDiv } from 'npo/pages/Tasks/TaskRow';
import TrueImpactButton from '../../design-system/components/TrueImpactButton';
import { COLORS } from '../../design-system/settings/variables/Colors';
import AllocateReportModal from './AllocateReportModal';

interface AllocateReportProps {
  report: Report;
}

const StyledDiv = styled.div`
  .tasks {
    box-sizing: border-box;
    max-width: 660px;
    margin: 0 auto;
    padding: ${SPACERS.medium};
    .task-description {
      margin-bottom: ${SPACERS.medium};
    }
    .task-row-wrapper {
      padding: ${SPACERS.medium};
      margin-bottom: ${SPACERS.medium};
      background-color: ${COLORS.whiteBase};
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    .allocate-button {
      // width: 200px;
    }
  }
`;

const Content: React.FC<AllocateReportProps & {
  report: Report;
  requestState: RequestState<{
    unallocatedInvestments: UnallocatedInvestment[];
  }>;
  handleClose: () => unknown;
}> = ({ requestState, handleClose, report }) => {
  const { name } = useUserActions();
  const [openedTask, setOpenedTask] = useState<Task | undefined>(undefined);
  switch (requestState.type) {
    case 'REQUEST_INIT':
    case 'REQUEST_START':
      return (
        <StyledDiv>
          <TrueImpactLoading
            marginTop={0}
            marginBottom={0}
            paddingTop={30}
            paddingBottom={30}
          />
        </StyledDiv>
      );
    case 'REQUEST_SUCCESS': {
      const tasks = buildUnallocatedInvestmentTasks(
        requestState.data.unallocatedInvestments,
      ).filter(
        task =>
          task.status !== 'COMPLETED' && task.invitation?.investment?.donation,
      );
      // If no tasks, modal won't open (handled in parent component)
      if (tasks.length === 0) {
        return null;
      }
      return (
        <StyledDiv>
          <div className="tasks">
            <TrueImpactText className="task-description">
              There are currently unallocated donations in your account. If this
              report intended to be shared with a funder and required as a
              condition of a donation, please ensure you allocate the funder’s
              donation accordingly.
            </TrueImpactText>
            {tasks.map(task => {
              const handleStart = () => {
                setOpenedTask(task);
              };
              return (
                <div className="task-row-wrapper" key={task.taskId}>
                  <StyledTaskDiv
                    isCompleted={false}
                    isOverdue={task.status === 'OVERDUE'}
                    isSelected={false}
                  >
                    <div className="task-row">
                      <div className="task-row-details">
                        {getAllocationTaskDescription(task)}
                        {getTaskMeta(task, undefined, name)}
                      </div>
                      <div className="buttons-wrapper">
                        <TrueImpactButton
                          onClick={handleStart}
                          className="allocate-button"
                        >
                          Allocate
                        </TrueImpactButton>
                      </div>
                    </div>
                  </StyledTaskDiv>
                </div>
              );
            })}
            <AllocateReportModal
              report={report}
              task={openedTask}
              onClose={() => setOpenedTask(undefined)}
            />
          </div>
        </StyledDiv>
      );
    }
    default:
    case 'REQUEST_ERROR':
      return (
        <StyledDiv>
          <TrueImpactText color="critical">
            Something went wrong.
          </TrueImpactText>
        </StyledDiv>
      );
  }
};

const styles = `.modal-header { border-bottom: 1px solid ${COLORS.ctaPrimaryBackground}; .close { padding-bottom: 0; } .modal-title { ` +
  `padding-bottom: ${SPACERS.small} } }`;

const AllocateReport: React.FC<AllocateReportProps> = ({ report }) => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest<{
    unallocatedInvestments: UnallocatedInvestment[];
  }>();
  const [open, setOpen] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    if (!client) return;
    makeRequest(getUnallocatedInvestmentsQuery(client));
  }, [makeRequest, client]);

  // Only open modal if there are pending allocation tasks
  useEffect(() => {
    if (requestState.type === 'REQUEST_SUCCESS') {
      const tasks = buildUnallocatedInvestmentTasks(
        requestState.data.unallocatedInvestments,
      ).filter(
        task =>
          task.status !== 'COMPLETED' && task.invitation?.investment?.donation,
      );
      if (tasks.length > 0) {
        setOpen(true);
      }
    }
  }, [requestState]);

  if (!open) {
    return null;
  }

  return (
    <TrueImpactModal
      open={open}
      title="Allocate donation to this report"
      maxWidth="1000px"
      maxHeight="900px"
      hideFooter={true}
      showExpandButton={true}
      addStyles={modelExtraRadiusStyles + styles}
      onClose={handleClose}
    >
      <Content
        report={report}
        handleClose={handleClose}
        requestState={requestState}
      />
    </TrueImpactModal>
  );
};

export default AllocateReport;
